import {Edge} from 'reactflow';
import uuid from 'uuid-random';

import {
  DecisionTreeNodeDataMultiChoiceOptionFrontChat,
  NodeDataFrontChatTypesEnum,
} from '../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {
  addSourceHandleEdges,
  computeTreeData,
  makeEdge,
  nextNodePositionFromDirection,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {
  DecisionTreeEdgeTypesEnum,
  DecisionTreeNodeTypesEnum,
  TreeData,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {chatBotBuilderDirection} from './constants';
import {ChatBotBuilderNode, ChatBotBuilderNodeData} from './types';

/*
 * Helpers.
 */

/**
 * Creates a default tree by setting up the default multiple choice node and applying the processors
 * to create the edges which correspond to the sourceHandle of each option.
 *
 * This should not be used in production. Instead, use this function to generate a tree state and
 * allow the useReactFlowLayout hook to adjust the nodes and edges. The final adjusted state is
 * what should be stored below, to later initialize our Decision Tree.
 */
export function makeDefaultChatBotBuilderTreeState(): TreeData<ChatBotBuilderNodeData> {
  const multiChoiceOptions: ReadonlyArray<DecisionTreeNodeDataMultiChoiceOptionFrontChat> = [
    {
      value: 'Talk to sales',
      sourceHandle: uuid(),
    },
    {
      value: 'Contact support',
      sourceHandle: uuid(),
    },
  ];

  const nodes: Array<ChatBotBuilderNode> = [
    {
      id: '0',
      data: {type: NodeDataFrontChatTypesEnum.START},
      position: {x: 0, y: 0},
      type: DecisionTreeNodeTypesEnum.WORKFLOW,
    },
    {
      id: '1',
      data: {
        type: NodeDataFrontChatTypesEnum.MULTI_CHOICE,
        message: 'Hi there, how can we help?',
        options: multiChoiceOptions,
      },
      position: nextNodePositionFromDirection(chatBotBuilderDirection, {x: 0, y: 0}),
      type: DecisionTreeNodeTypesEnum.WORKFLOW,
    },
  ];

  const edges: Array<Edge> = [makeEdge(nodes[0].id, nodes[1].id, DecisionTreeEdgeTypesEnum.WORKFLOW)];

  const addedHandleState = addSourceHandleEdges(
    chatBotBuilderDirection,
    nodes,
    edges,
    '1',
    multiChoiceOptions.map((option) => option.sourceHandle),
    {type: NodeDataFrontChatTypesEnum.END},
  );

  return {nodes: addedHandleState.nodes, edges: addedHandleState.edges};
}

/*
 * Pre-processed default tree.
 */

/**
 * Do not modify this manually, except to fix any type issues. This should be created by using the above function,
 * makeDefaultChatBotBuilderTreeState, and then allowing useReactFlowLayout to adjust the node positions.
 * The output is passed through computeTreeData below to ensure no extraneous fields make it through.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const defaultChatBotBuilderTreeState = Object.freeze(
  computeTreeData(
    [
      {
        id: '0',
        type: 'workflow',
        position: {
          x: 0,
          y: 0,
        },
        data: {
          type: 'start',
        },
      },
      {
        id: '1',
        type: 'workflow',
        position: {
          x: 162.5,
          y: 0,
        },
        data: {
          type: 'multi_choice',
          message: 'Hi there, how can we help?',
          options: [
            {
              value: 'Talk to sales',
              sourceHandle: '76e61162-98b2-49fd-a8a8-7958e79cebce',
            },
            {
              value: 'Contact support',
              sourceHandle: '77417443-6f6d-4974-a8f1-48b9a7c003cf',
            },
          ],
        },
      },
      {
        id: '4e73d1b2-4525-47a1-b3f6-5df5d57fd08f',
        type: 'placeholder',
        position: {
          x: 487.5,
          y: -87.5,
        },
        data: {
          type: 'end',
        },
      },
      {
        id: '98a8f5e9-676d-49e0-be35-d4a17bd6228f',
        type: 'placeholder',
        position: {
          x: 487.5,
          y: 87.5,
        },
        data: {
          type: 'end',
        },
      },
    ],

    [
      {
        id: '0=>1',
        type: 'workflow',
        source: '0',
        target: '1',
        sourceHandle: '0=>1',
      },
      {
        id: '1=>4e73d1b2-4525-47a1-b3f6-5df5d57fd08f',
        type: 'placeholder',
        source: '1',
        target: '4e73d1b2-4525-47a1-b3f6-5df5d57fd08f',
        sourceHandle: '76e61162-98b2-49fd-a8a8-7958e79cebce',
      },
      {
        id: '1=>98a8f5e9-676d-49e0-be35-d4a17bd6228f',
        type: 'placeholder',
        source: '1',
        target: '98a8f5e9-676d-49e0-be35-d4a17bd6228f',
        sourceHandle: '77417443-6f6d-4974-a8f1-48b9a7c003cf',
      },
    ],
  ),
) as TreeData<ChatBotBuilderNodeData>;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const defaultChatBotLimitedBuilderTreeState = Object.freeze(
  computeTreeData(
    [
      {
        id: '0',
        type: 'workflow',
        position: {
          x: 0,
          y: 0,
        },
        data: {
          type: 'start',
        },
      },
      {
        id: '2032c082-c042-43fd-8f3a-d800568c8be3',
        type: 'workflow',
        position: {
          x: 162.5,
          y: 0,
        },
        data: {
          type: 'message',
          message: 'Hi there! How can we help you?',
        },
      },
      {
        id: 'ff92b728-1259-45ef-9687-839b3065eea3',
        type: 'workflow',
        height: 200,
        width: 240,
        position: {
          x: 487.5,
          y: 0,
        },
        data: {
          type: 'custom_fields',
          customFields: [
            {
              customFieldId: -1,
              fieldName: 'Name',
              fieldType: 'string',
              isRequired: true,
              resourceType: 'card',
            },
            {
              customFieldId: -2,
              fieldName: 'Email',
              fieldType: 'email',
              isRequired: false,
              resourceType: 'card',
            },
          ],
        },
      },
      {
        id: '872a666f-a99c-4048-bcca-671aebd7d7e7',
        type: 'workflow',
        height: 200,
        width: 240,
        position: {
          x: 812.5,
          y: 0,
        },
        data: {
          type: 'prompt',
          message: 'How can we help?',
        },
      },
      {
        id: 'c30c46cb-b953-42ef-81b6-610369ec8ccb',
        type: 'placeholder',
        position: {
          x: 1137.5,
          y: 0,
        },
        data: {
          type: 'end',
        },
      },
    ],
    [
      {
        id: '0=>2032c082-c042-43fd-8f3a-d800568c8be3',
        type: 'workflow',
        source: '0',
        target: '2032c082-c042-43fd-8f3a-d800568c8be3',
        sourceHandle: '0=>1',
      },
      {
        id: '2032c082-c042-43fd-8f3a-d800568c8be3=>ff92b728-1259-45ef-9687-839b3065eea3',
        type: 'workflow',
        source: '2032c082-c042-43fd-8f3a-d800568c8be3',
        target: 'ff92b728-1259-45ef-9687-839b3065eea3',
        sourceHandle: '2032c082-c042-43fd-8f3a-d800568c8be3=>c30c46cb-b953-42ef-81b6-610369ec8ccb',
      },
      {
        id: 'ff92b728-1259-45ef-9687-839b3065eea3=>872a666f-a99c-4048-bcca-671aebd7d7e7',
        type: 'workflow',
        source: 'ff92b728-1259-45ef-9687-839b3065eea3',
        target: '872a666f-a99c-4048-bcca-671aebd7d7e7',
        sourceHandle: 'ff92b728-1259-45ef-9687-839b3065eea3=>c30c46cb-b953-42ef-81b6-610369ec8ccb',
      },
      {
        id: '872a666f-a99c-4048-bcca-671aebd7d7e7=>c30c46cb-b953-42ef-81b6-610369ec8ccb',
        type: 'workflow',
        source: '872a666f-a99c-4048-bcca-671aebd7d7e7',
        target: 'c30c46cb-b953-42ef-81b6-610369ec8ccb',
        sourceHandle: '872a666f-a99c-4048-bcca-671aebd7d7e7=>c30c46cb-b953-42ef-81b6-610369ec8ccb',
      },
    ],
  ),
) as TreeData<ChatBotBuilderNodeData>;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const defaultChatBotBuilderSubtreeState = Object.freeze(
  computeTreeData(
    [
      {
        id: '0',
        data: {type: NodeDataFrontChatTypesEnum.START},
        position: {x: 0, y: 0},
        type: DecisionTreeNodeTypesEnum.WORKFLOW,
      },
      {
        width: 160,
        height: 43,
        id: '1',
        position: {
          x: 162.5,
          y: 0,
        },
        data: {
          type: NodeDataFrontChatTypesEnum.MESSAGE,
          message: 'This is a saved flow. Try adding more steps!',
        },
        type: DecisionTreeNodeTypesEnum.WORKFLOW,
      },
      {
        id: '2',
        type: 'placeholder',
        position: {
          x: 487.5,
          y: 0,
        },
        data: {
          type: 'end',
        },
      },
    ],
    [
      {
        id: '0=>1',
        type: 'workflow',
        source: '0',
        target: '1',
        sourceHandle: '0=>1',
      },
      {
        id: '1=>2',
        type: 'workflow',
        source: '1',
        target: '2',
        sourceHandle: '1=>2',
      },
    ],
  ),
) as TreeData<ChatBotBuilderNodeData>;
